import { useApolloClient } from '@apollo/client';
import { parse } from '@jupiterone/query-language-parser';
import React, { useEffect, useMemo } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { deferredQuery } from '../../clients/deferredQuery';
import { DeferredQueryInput } from '../../hooks/graphql/useDeferredJ1QLQuery';
import {
  DeferredResponseFormat,
  J1QLResultData,
  isJ1QLListData,
} from '../../../types/J1QL';
import { useSnackbarNotification } from '../notifications/SnackbarNotificationContext';
import { ResultFormat } from './types';
import {
  useMockHook,
  mockFunction,
} from '@jupiterone/jupiter-web-toolkit/src/util/useMockHook';

export interface HookProps {
  queryInput?: DeferredQueryInput;
  result: J1QLResultData;
}

const useQueryResultLogic = ({ queryInput, result }: HookProps) => {
  const notification = useSnackbarNotification();
  const client = useApolloClient();

  const data = result.data;

  const [selectedFormat, setSelectedFormat] = React.useState<ResultFormat>(
    ResultFormat.Table
  );

  useEffect(() => {
    setSelectedFormat(
      result.type === 'tree' ? ResultFormat.Graph : ResultFormat.Table
    );
  }, [result.type]);

  const exporting = useBooleanState(false);

  const { query = '' } = queryInput || {};
  const hasNextPage = useMemo(() => {
    // paginate at 250, dont paginate with limit present
    if (parse(query)?.limit) return false;
    return Boolean(result.cursor);
  }, [query, isJ1QLListData(result) ? result?.data?.length : result.data]);

  const handleSelectedDownload = async (
    deferredFormat: DeferredResponseFormat
  ) => {
    try {
      exporting.setTrue();
      const { url } = await deferredQuery(client, {
        query,
        deferredFormat,
        flags: { allPages: parse(query).limit === undefined },
        ...queryInput,
      });
      if (url) {
        const win = window.open(url, '_blank', 'noopener');
        win?.focus();
      }
    } catch (err) {
      notification.error({
        message: err.message ? err.message : 'Download could not be completed.',
        error: err,
      });
    } finally {
      exporting.setFalse();
    }
  };

  const hasResults =
    (Array.isArray(data) && data.length !== 0) ||
    (!Array.isArray(data) && data?.vertices && data?.vertices.length !== 0);

  return {
    query,
    selectedFormat,
    exporting,
    hasResults,
    hasNextPage,
    setSelectedFormat,
    handleSelectedDownload,
  };
};

const mockData = {
  query: '',
  selectedFormat: ResultFormat.Table,
  exporting: {
    setTrue: mockFunction,
    setFalse: mockFunction,
    toggle: mockFunction,
    setState: mockFunction,
    state: false,
  },
  hasResults: false,
  hasNextPage: false,
  setSelectedFormat: mockFunction,
  handleSelectedDownload: mockFunction,
};

export default useMockHook(useQueryResultLogic, mockData);
